/**
 * The level loading overlay (SPEC §5, reference 04): while a level opens, the
 * viewport dims under the kn mark, the level's name, the step under way and a
 * bar of the load's real progress (rule 7). It stays up through the Shaders
 * step, so the new level appears ready instead of hitching, and for at least
 * `holdMs`, so a quick switch doesn't flash it; then it fades.
 *
 * Its motion is only `transform` and `opacity`, which the browser's
 * compositor runs even while the page's main thread or the GPU's shader
 * compiler is busy: the moments it's covering.
 *
 * The previous level stays on screen, dimmed, until the new one commits
 * (A-42). A failed open keeps the current level and says why (A-43).
 */
import { useState } from "react";
import { useLoadProgress } from "../hooks/editorHooks";
import type { LoadProgress } from "../levels/LevelService";
import { LevelIcon } from "../ui/icons";
import { currentStep, transitionFraction } from "./transitionModel";
import { useHeldVisibility } from "./useHeldVisibility";
import "./levelTransition.css";

/** The overlay's minimum time on screen, so a quick switch doesn't flash it. */
export const LOAD_HOLD_MS = 700;
/** Its fade out. */
export const LOAD_FADE_MS = 180;

export interface LevelTransitionProps {
  readonly holdMs?: number;
  readonly fadeMs?: number;
}

export function LevelTransition({
  holdMs = LOAD_HOLD_MS,
  fadeMs = LOAD_FADE_MS,
}: LevelTransitionProps) {
  const progress = useLoadProgress();
  const [dismissed, setDismissed] = useState<LoadProgress | null>(null);
  const visibility = useHeldVisibility(progress.phase === "loading", holdMs, fadeMs);
  // What the overlay shows while it's held or fading: the load as it last was.
  const [shown, setShown] = useState(progress);
  if (progress.phase === "loading" && progress !== shown) setShown(progress);

  if (progress.phase === "failed" && dismissed !== progress) {
    return (
      <FailureCard
        progress={progress}
        onDismiss={() => {
          setDismissed(progress);
        }}
      />
    );
  }
  if (visibility === "hidden") return null;
  return <LoadingOverlay progress={shown} leaving={visibility === "leaving"} />;
}

function LoadingOverlay({ progress, leaving }: { progress: LoadProgress; leaving: boolean }) {
  // Held past the load's end, the bar shows it finished.
  const fraction = progress.phase === "loading" && !leaving ? transitionFraction(progress) : 1;
  const title = `Opening ${progress.name}`;

  return (
    <div className="kn-transition" data-leaving={leaving || undefined}>
      <div className="kn-loading" role="status" aria-label={title}>
        <LoadingMark />
        <h2 className="kn-loading__title">{title}</h2>
        <p className="kn-loading__step">{leaving ? "" : currentStep(progress)}</p>
        <div
          className="kn-loading__bar"
          role="progressbar"
          aria-label="Level load"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(fraction * 100)}
        >
          <div className="kn-loading__fill" style={{ transform: `scaleX(${fraction})` }} />
        </div>
      </div>
    </div>
  );
}

/** The kn diamond, with a light sweeping round its outline and a pulse at its heart. */
function LoadingMark() {
  return (
    <div className="kn-loading__mark" aria-hidden="true">
      <span className="kn-loading__ring" />
      <span className="kn-loading__sweep" />
      <span className="kn-loading__core" />
    </div>
  );
}

function FailureCard({ progress, onDismiss }: { progress: LoadProgress; onDismiss: () => void }) {
  return (
    <div className="kn-transition kn-transition--failed">
      <div className="kn-transition__card" role="alert">
        <header className="kn-transition__header">
          <LevelIcon size={16} className="kn-transition__icon" />
          <div>
            <h2>Couldn&apos;t open {progress.name}</h2>
            <p className="kn-transition__path">{progress.error}</p>
          </div>
        </header>
        <button type="button" className="kn-transition__dismiss" onClick={onDismiss}>
          Dismiss
        </button>
      </div>
    </div>
  );
}
